import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect } from "react";
import { Play, Music2, MousePointer2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { Mascot } from "@/components/Mascot";
import { MaskLine } from "@/components/Reveal";
import { Sparkle, Note } from "@/components/icons";
import { scrollToId } from "@/components/SmoothScroll";

const RAINBOW = ["#ff7a2f", "#ff4d8d", "#38b6ff", "#2fb344", "#e63946", "#7c4dff", "#38b6ff"];

const Rainbow = ({ text }) => (
  <span className="inline-block">
    {text.split("").map((ch, i) => (
      <span key={i} style={{ color: RAINBOW[i % RAINBOW.length], WebkitTextStroke: "2px #1f1a3d", paintOrder: "stroke fill" }} className="inline-block [text-shadow:0_5px_0_#1f1a3d]">
        {ch === " " ? "\u00a0" : ch}
      </span>
    ))}
  </span>
);

const Float = ({ depth, mx, my, className, children }) => {
  const x = useTransform(mx, [-1, 1], [-30 * depth, 30 * depth]);
  const y = useTransform(my, [-1, 1], [-20 * depth, 20 * depth]);
  return (
    <motion.div style={{ x, y }} className={`absolute pointer-events-none ${className}`}>
      {children}
    </motion.div>
  );
};

const Sunburst = () => (
  <svg viewBox="0 0 400 400" className="w-full h-full spin-slow" aria-hidden="true">
    {Array.from({ length: 16 }).map((_, i) => (
      <path key={i} d="M200 200 L188 0 L212 0 Z" transform={`rotate(${i * 22.5} 200 200)`} fill={i % 2 ? "#ffd23f" : "#ffe482"} />
    ))}
    <circle cx="200" cy="200" r="120" fill="#ffd23f" />
  </svg>
);

export const Hero = () => {
  const { t, lang } = useI18n();
  const { content, tracks } = useSite();
  // Triple-click dance: only songs of the current page language.
  const allTracks = (Array.isArray(tracks) ? tracks : []).filter((x) => x.lang === lang);
  const fixed = allTracks.filter((x) => x.id === content?.dance_track_id && x.preview_url);
  const upbeat = allTracks.filter((x) => x.preview_url && !/nann|nana|lullaby|ninar/i.test(x.title));
  const danceTracks = fixed.length ? fixed : upbeat.length ? upbeat : allTracks.filter((x) => x.preview_url);
  const override = content?.texts?.[lang] ?? {};
  const lines = override.hero_title ? override.hero_title.split("|").map((s) => s.trim()) : t.hero.lines;
  const sub = override.hero_subtitle || t.hero.sub;
  const mxRaw = useMotionValue(0);
  const myRaw = useMotionValue(0);
  const mx = useSpring(mxRaw, { stiffness: 60, damping: 20 });
  const my = useSpring(myRaw, { stiffness: 60, damping: 20 });
  const stageY = useTransform(my, [-1, 1], [10, -10]);

  useEffect(() => {
    const onMove = (e) => {
      mxRaw.set((e.clientX / window.innerWidth) * 2 - 1);
      myRaw.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [mxRaw, myRaw]);

  const last = lines.length - 1;

  return (
    <section id="home" className="relative overflow-hidden grain dots-bg pt-28 sm:pt-32 pb-16 lg:pb-24 min-h-[100svh] flex items-center" data-testid="hero-section">
      <Float depth={1.4} mx={mx} my={my} className="top-28 left-[6%] hidden sm:block"><Sparkle className="w-10 h-10" color="#ff4d8d" /></Float>
      <Float depth={-1} mx={mx} my={my} className="top-[62%] left-[3%]"><Note className="w-9 h-9" color="#7c4dff" /></Float>
      <Float depth={2} mx={mx} my={my} className="bottom-16 left-[42%]"><Sparkle className="w-7 h-7" color="#38b6ff" /></Float>
      <Float depth={-1.6} mx={mx} my={my} className="top-32 right-[8%]"><Note className="w-10 h-10" color="#ff7a2f" /></Float>
      <Float depth={0.8} mx={mx} my={my} className="bottom-24 right-[4%] hidden md:block"><Sparkle className="w-12 h-12" color="#2fb344" /></Float>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full sticker-sm bg-white px-4 py-1.5 text-xs sm:text-sm font-extrabold uppercase tracking-wider"
            data-testid="hero-eyebrow"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-leaf animate-pulse" /> {t.hero.eyebrow}
          </motion.span>
          <h1 className="font-display font-bold mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink" data-testid="hero-title">
            {lines.map((l, i) => (
              <MaskLine key={`${lang}-${i}`} delay={0.15 + i * 0.13}>
                {i === last ? <Rainbow text={l} /> : l}
              </MaskLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-ink/80"
            data-testid="hero-subtitle"
          >
            {sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }} className="mt-8 flex flex-wrap gap-4">
            <button onClick={() => scrollToId("video")} className="btn-pop sticker inline-flex items-center gap-2 rounded-full bg-pink text-white px-6 py-3.5 font-display font-semibold text-lg" data-testid="hero-watch-btn">
              <Play className="w-5 h-5 fill-white" /> {t.hero.watch}
            </button>
            <button onClick={() => scrollToId("musica")} className="btn-pop sticker inline-flex items-center gap-2 rounded-full bg-sun text-ink px-6 py-3.5 font-display font-semibold text-lg" data-testid="hero-listen-btn">
              <Music2 className="w-5 h-5" /> {t.hero.listen}
            </button>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="mt-8 hidden sm:flex items-center gap-2 text-sm font-bold text-ink/70">
            <MousePointer2 className="w-4 h-4" /> {t.hero.hint}
          </motion.p>
        </div>

        <motion.div
          style={{ y: stageY }}
          initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.3, type: "spring", bounce: 0.4 }}
          className="lg:col-span-5 relative mx-auto w-full max-w-[340px] sm:max-w-[420px] lg:max-w-[480px]"
        >
          <div className="absolute inset-[-14%] opacity-90"><Sunburst /></div>
          <div className="absolute inset-[6%] rounded-full border-[3px] border-dashed border-ink/30" />
          <Mascot bubbles={t.bubbles} danceTracks={danceTracks} danceHint={t.auth.dance} className="relative z-10 px-[8%] pt-[6%]" />
        </motion.div>
      </div>
    </section>
  );
};
